import React, { createContext, useContext, useState, ReactNode } from 'react';

interface LanguageContextType {
  language: string;
  setLanguage: (lang: string) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const TRANSLATIONS = {
  ko: {
    'app.title': '뽀꼬톡',
    'nav.home': '홈',
    'nav.chat': '쏘울',
    'nav.activity': '액티비티',
    'nav.routine': '루틴',
    'nav.emotion.release': '감정해소',
    'nav.privacy': '개인정보처리방침',
    'nav.terms': '이용약관',
    'language.selector': '언어 선택',
    'welcome.title': '뽀꼬톡에 오신 것을 환영합니다',
    'welcome.subtitle': 'AI와 함께 감정을 탐색하고 마음을 돌보세요',
    'chat.placeholder': '메시지를 입력하세요...',
    'chat.send': '보내기',
    'chat.typing': '쏘울이 응답 중입니다...',
    'activity.select': '활동을 선택하세요',
    'activity.complete': '완료',
    'routine.title': '루틴 완료',
    'routine.emotion': '감정',
    'routine.activity': '활동',
    'routine.continue': '쏘울로 이어가기',
    'error.api': 'API 호출 중 오류가 발생했습니다',
    'error.network': '네트워크 오류가 발생했습니다',
    'home.main.title': '마음을 다스리고 평온을 찾는 시간',
    'home.main.subtitle': '뽀꼬톡과 함께 일상의 감정을 정리하고, 내면의 평화를 찾아보세요',
    'home.soul.title': '쏘울과 대화하기',
    'home.soul.subtitle': '당신의 친구 \'쏘울\'과 편하게 이야기 해보세요',
    'home.recommended.title': '추천 루틴',
    'home.recommended.subtitle': '하루를 더 의미있게 만들어주는 루틴을 시작해보세요',
    'home.start': '시작하기',
    'home.routine.start': '루틴 시작!',
    'home.routine.preparing': '쏘울과의 대화를 준비하고 있습니다',
    'home.wait': '잠시만 기다려주세요...',
    'home.morning.routine': '뽀꼬 모닝',
    'home.morning.description': '나만의 하루를 시작하는 시간',
    'home.focus.routine': '뽀꼬 뽀꺼스',
    'home.focus.description': '마음을 다스리고 집중하는 시간',
    'home.evening.routine': '뽀꼬 쌩스',
    'home.evening.description': '하루를 마무리하며 감사하는 시간',
    'chat.welcome': '안녕하세요! 쏘울입니다. 오늘 하루는 어떠셨나요? 편하게 이야기해주세요.',
    'chat.start.conversation': '쏘울과 대화를 시작해보세요',
    'chat.emotion.detected': '감정이 파악되었습니다',
    'chat.emotion.expression': '어떤 활동으로 감정을 표현하고 싶으신가요?',
    'chat.ai.soul.description': '당신의 친구 \'쏘울\'과 편하게 이야기 해보세요',
    'activity.title': '액티비티',
    'activity.subtitle': '다양한 활동을 통해 감정을 표현하고 해소해보세요',
    'activity.completion.title': '완료!',
    'activity.completion.emotion.resolved': '을(를) {activity}로 해소했습니다',
    'activity.completion.summary': '활동 요약',
    'activity.completion.continue': '쏘울로 이어가기',
    'activity.completion.another': '다른 활동하기',
    'activity.sketch.title': '스케치 (준비 중)',
    'activity.sketch.title.coming': '스케치 기능은 곧 추가될 예정입니다',
    'activity.voice.title': '음성 녹음 (준비 중)',
    'activity.voice.title.coming': '음성 녹음 기능은 곧 추가될 예정입니다',
    'activity.meditation.name': '명상',
    'activity.meditation.description': '마음을 정리하고 평온을 찾아보세요',
    'activity.breathing.name': '심호흡',
    'activity.breathing.description': '깊은 호흡으로 마음을 가라앉혀보세요',
    'activity.piano.name': '피아노',
    'activity.piano.description': '음악으로 감정을 표현해보세요',
    'activity.sketch.name': '스케치',
    'activity.sketch.description': '그림으로 마음을 그려보세요',
    'activity.voice.name': '음성',
    'activity.voice.description': '음성으로 감정을 녹음해보세요',
    'activity.writing.name': '글쓰기',
    'activity.writing.description': '글쓰기로 생각을 정리해보세요',
    'routine.subtitle': '기본 루틴을 시작하고 쏘울과 대화해보세요',
    'routine.start': '시작하기',
    'routine.starting': '시작!',
    'routine.preparing': '쏘울과의 대화를 준비하고 있습니다...',
    'routine.morning.title': '뽀꼬 모닝',
    'routine.morning.description': '나만의 하루를 시작하는 시간',
    'routine.focus.title': '뽀꼬 뽀꺼스',
    'routine.focus.description': '마음을 다스리고 집중하는 시간',
    'routine.evening.title': '뽀꼬 쌩스',
    'routine.evening.description': '하루를 마무리하며 감사하는 시간',
    'popup.coming.soon.title': '준비중 입니다...',
    'popup.coming.soon.description': '곧 서비스가 시작됩니다',
    // Privacy Policy
    'privacy.title': '개인정보 처리방침',
    'privacy.1.title': '제1조 (수집하는 개인정보 항목)',
    'privacy.1.item.required': '필수 항목: 이메일, 닉네임, 비밀번호(암호화 저장)',
    'privacy.1.item.optional': '선택 항목: 프로필 사진, 감정 기록, 게시물(텍스트, 이미지, 오디오 등)',
    'privacy.1.item.auto': '자동 수집 항목: 기기 정보, 접속 로그, 쿠키, 사용 기록',
    'privacy.2.title': '제2조 (개인정보의 이용 목적)',
    'privacy.2.item.service': '서비스 제공 및 운영',
    'privacy.2.item.content': '사용자 콘텐츠 게시 및 공유',
    'privacy.2.item.monitoring': '부적절한 콘텐츠 모니터링 및 차단',
    'privacy.2.item.improvement': '서비스 개선 및 고객 지원',
    'privacy.3.title': '제3조 (개인정보의 보관 및 파기)',
    'privacy.3.item.delete': '개인정보는 회원 탈퇴 시 즉시 삭제됩니다',
    'privacy.3.item.law': '단, 법령에 따라 일정 기간 보관이 필요한 경우 그에 따릅니다',
    'privacy.4.title': '제4조 (이용자의 권리)',
    'privacy.4.item.access': '이용자는 언제든지 자신의 개인정보를 열람·수정·삭제할 수 있습니다',
    'privacy.4.item.deletion': '원할 경우 계정 삭제 및 서비스 탈퇴를 요청할 수 있습니다',
    'privacy.5.title': '제5조 (제3자 제공 및 위탁)',
    'privacy.5.item.principle': '서비스는 이용자의 개인정보를 원칙적으로 제3자에게 제공하지 않습니다',
    'privacy.5.item.entrust': '다만, 클라우드 서버, 데이터 분석 등 일부 기능은 외부 업체에 위탁될 수 있습니다',
    'privacy.6.title': '제6조 (안전성 확보 조치)',
    'privacy.6.item.security': '서비스는 암호화, 접근권한 제한, 보안 모니터링 등을 통해 개인정보를 안전하게 관리합니다',
    'privacy.7.title': '제7조 (계정 삭제)',
    'privacy.7.item.menu': '이용자는 앱 내 [설정 → 프로필 편집 → 계정 삭제] 메뉴를 통해 언제든 계정을 삭제할 수 있습니다',
    'privacy.7.item.immediate': '계정 삭제 시 모든 개인정보는 즉시 파기됩니다',
    // Terms of Service
    'terms.title': '서비스 이용약관',
    'terms.intro': '이용자는 회원 가입 시 본 약관과 개인정보 처리방침에 동의해야 하며, 동의하지 않을 경우 서비스 이용이 제한됩니다',
    'terms.1.title': '제1조 (목적)',
    'terms.1.item.purpose': '본 약관은 이용자가 뽀꼬톡(PocoTalk, 이하 "서비스")을 이용함에 있어 회사와 이용자 간의 권리, 의무 및 책임 사항을 규정함을 목적으로 합니다',
    'terms.2.title': '제2조 (이용자의 의무)',
    'terms.2.item.prohibited': '이용자는 다음 행위를 해서는 안 됩니다',
    'terms.2.item.illegal': '불법, 음란, 혐오, 폭력적 또는 차별적 콘텐츠 게시',
    'terms.2.item.rights': '타인의 개인정보, 저작권 등 권리를 침해하는 행위',
    'terms.2.item.spam': '스팸, 광고성 글 또는 시스템 오용',
    'terms.2.item.consequence': '위반 시 서비스 이용이 제한될 수 있으며, 법적 책임이 따를 수 있습니다',
    'terms.3.title': '제3조 (사용자 생성 콘텐츠 관리)',
    'terms.3.item.monitoring': '서비스는 이용자가 올린 콘텐츠를 모니터링·필터링할 수 있습니다',
    'terms.3.item.deletion': '모든 콘텐츠는 내부 정책 및 법령을 위반할 경우 사전 통보 없이 삭제될 수 있습니다',
    'terms.3.item.features': '서비스는 이용자에게 다음 기능을 제공합니다',
    'terms.3.item.flagging': '부적절한 콘텐츠를 신고(Flagging)할 수 있는 기능',
    'terms.3.item.blocking': '특정 이용자를 차단(Block)할 수 있는 기능',
    'terms.4.title': '제4조 (면책)',
    'terms.4.item.liability': '서비스는 이용자가 작성한 콘텐츠에 대해 법적 책임을 지지 않으며, 해당 책임은 전적으로 이용자 본인에게 있습니다',
    'terms.5.title': '5. 의료·심리 상담 아님',
    'terms.5.item.no.advice': '본 서비스는 전문 의료·심리 상담을 대체하지 않습니다',
    'terms.5.item.seek.help': '건강 관련 문제는 반드시 전문가의 도움을 받으세요',
    'terms.5.item.own.risk': '사용은 전적으로 사용자 책임 하에 이루어집니다',
    'terms.6.title': '6. 서비스 변경 및 종료',
    'terms.6.item.modify': '회사는 서비스의 전부 또는 일부를 변경, 중단할 수 있습니다',
    'terms.6.item.terminate': '약관 위반 시 계정 제한 또는 해지될 수 있습니다',
    'terms.7.title': '7. 책임의 제한',
    'terms.7.item.indirect': '간접적·부수적 손해에 대해 회사는 책임을 지지 않습니다',
    'terms.7.item.cap': '법이 허용하는 범위 내에서 회사의 총 책임은 최근 3개월간 사용자 지급액(지급액이 없을 경우 0)을 한도로 합니다',
    'terms.8.title': '8. 준거법 및 분쟁',
    'terms.8.item.law': '본 약관은 대한민국 법령에 따릅니다',
    'terms.8.item.dispute': '분쟁은 관할 법원에서 해결합니다',
    'terms.9.title': '9. 문의처',
    'terms.9.item.email': '이메일: hello@pocotalk.app'
  },
  en: {
    'app.title': 'PocoTalk',
    'nav.home': 'Home',
    'nav.chat': 'Soul',
    'nav.activity': 'Activity',
    'nav.routine': 'Routine',
    'nav.emotion.release': 'Emotion Release',
    'nav.privacy': 'Privacy Policy',
    'nav.terms': 'Terms of Service',
    'language.selector': 'Select Language',
    'welcome.title': 'Welcome to PocoTalk',
    'welcome.subtitle': 'Explore emotions and take care of your mind with AI',
    'chat.placeholder': 'Enter your message...',
    'chat.send': 'Send',
    'chat.typing': 'Soul is responding...',
    'activity.select': 'Select an activity',
    'activity.complete': 'Complete',
    'routine.title': 'Routine Complete',
    'routine.emotion': 'Emotion',
    'routine.activity': 'Activity',
    'routine.continue': 'Continue to Soul',
    'error.api': 'An error occurred during API call',
    'error.network': 'Network error occurred',
    'home.main.title': 'Time to calm your mind and find peace',
    'home.main.subtitle': 'Organize your daily emotions and find inner peace with PocoTalk',
    'home.soul.title': 'Talk with Soul',
    'home.soul.subtitle': 'Have a warm conversation with your friend \'Soul\'',
    'home.recommended.title': 'Recommended Routines',
    'home.recommended.subtitle': 'Start routines that make your day more meaningful',
    'home.start': 'Start',
    'home.routine.start': 'Routine Started!',
    'home.routine.preparing': 'Preparing conversation with Soul',
    'home.wait': 'Please wait a moment...',
    'home.morning.routine': 'Poco Morning',
    'home.morning.description': 'Time to start your own day',
    'home.focus.routine': 'Poco Focus',
    'home.focus.description': 'Time to calm your mind and focus',
    'home.evening.routine': 'Poco Thanks',
    'home.evening.description': 'Time to end the day with gratitude',
    'chat.welcome': 'Hello! I\'m Soul. How was your day today? Feel free to talk to me.',
    'chat.start.conversation': 'Start a conversation with Soul',
    'chat.emotion.detected': 'Emotion detected',
    'chat.emotion.expression': 'What activity would you like to express your emotion through?',
    'chat.ai.soul.description': 'Have a warm conversation with your friend \'Soul\'',
    'activity.title': 'Activity',
    'activity.subtitle': 'Express and resolve emotions through various activities',
    'activity.completion.title': 'Complete!',
    'activity.completion.emotion.resolved': 'resolved {emotion} through {activity}',
    'activity.completion.summary': 'Activity Summary',
    'activity.completion.continue': 'Continue to Soul',
    'activity.completion.another': 'Try Another Activity',
    'activity.sketch.title': 'Sketch (Coming Soon)',
    'activity.sketch.title.coming': 'Sketch feature will be added soon',
    'activity.voice.title': 'Voice Recording (Coming Soon)',
    'activity.voice.title.coming': 'Voice recording feature will be added soon',
    'activity.meditation.name': 'Meditation',
    'activity.meditation.description': 'Organize your mind and find peace',
    'activity.breathing.name': 'Deep Breathing',
    'activity.breathing.description': 'Calm your mind with deep breathing',
    'activity.piano.name': 'Piano',
    'activity.piano.description': 'Express emotions through music',
    'activity.sketch.name': 'Sketch',
    'activity.sketch.description': 'Draw your mind with pictures',
    'activity.voice.name': 'Voice',
    'activity.voice.description': 'Record emotions with your voice',
    'activity.writing.name': 'Writing',
    'activity.writing.description': 'Organize thoughts through writing',
    'routine.subtitle': 'Start a basic routine and chat with Soul',
    'routine.start': 'Start',
    'routine.starting': 'Started!',
    'routine.preparing': 'Preparing conversation with Soul...',
    'routine.morning.title': 'Poco Morning',
    'routine.morning.description': 'Time to start your own day',
    'routine.focus.title': 'Poco Focus',
    'routine.focus.description': 'Time to calm your mind and focus',
    'routine.evening.title': 'Poco Evening',
    'routine.evening.description': 'Time to end the day with gratitude',
    'popup.coming.soon.title': 'Coming Soon...',
    'popup.coming.soon.description': 'Service will be available soon',
    // Privacy Policy
    'privacy.title': 'Privacy Policy',
    'privacy.1.title': 'Article 1 (Personal Information Items Collected)',
    'privacy.1.item.required': 'Required items: Email, nickname, password (encrypted storage)',
    'privacy.1.item.optional': 'Optional items: Profile picture, emotion records, posts (text, images, audio, etc.)',
    'privacy.1.item.auto': 'Automatically collected items: Device information (model, OS), access logs, cookies',
    'privacy.2.title': 'Article 2 (Purpose of Personal Information Use)',
    'privacy.2.item.service': 'Service provision and operation',
    'privacy.2.item.content': 'User content posting and sharing',
    'privacy.2.item.monitoring': 'Inappropriate content monitoring and blocking',
    'privacy.2.item.improvement': 'Service improvement and customer support',
    'privacy.3.title': 'Article 3 (Retention and Disposal of Personal Information)',
    'privacy.3.item.delete': 'Immediately deleted upon account deletion',
    'privacy.3.item.law': 'If retention is required by law, retained for the mandated period',
    'privacy.4.title': 'Article 4 (User Rights)',
    'privacy.4.item.access': 'Users can view, modify, and delete their personal information at any time',
    'privacy.4.item.deletion': 'Immediate processing upon account deletion request',
    'privacy.5.title': 'Article 5 (Third Party Provision and Outsourcing)',
    'privacy.5.item.principle': 'Not provided to third parties in principle',
    'privacy.5.item.exception': 'Exceptionally provided when legally required (e.g., requests from law enforcement)',
    'privacy.6.title': 'Article 6 (Security Measures)',
    'privacy.6.item.security': 'Server and network security systems in operation',
    'privacy.7.title': 'Article 7 (Account Deletion)',
    'privacy.7.item.menu': 'Users can delete their account at any time through the app\'s [Settings → Profile Edit → Account Delete] menu',
    'privacy.7.item.immediate': 'All personal information is disposed of immediately when the account is deleted',
    // Terms of Service
    'terms.title': 'Terms of Service',
    'terms.intro': 'By using the Service, you agree to these Terms and Privacy Policy, and if you do not agree, you may not use the Service.',
    'terms.1.title': '1. Purpose',
    'terms.1.item.purpose': 'This Agreement aims to regulate the rights, obligations, and responsibilities between the Company and users in connection with the use of the Service.',
    'terms.2.title': '2. User Obligations',
    'terms.2.item.prohibited': 'Users must not engage in the following actions:',
    'terms.2.item.illegal': 'Illegal, obscene, hateful, violent, or discriminatory content posting',
    'terms.2.item.rights': 'Actions that infringe on the personal information, copyrights, etc. of others',
    'terms.2.item.spam': 'Spam, advertising-like posts, or system abuse',
    'terms.2.item.consequence': 'Violations may result in restrictions on service use and legal liability.',
    'terms.3.title': '3. User-Generated Content Management',
    'terms.3.item.monitoring': 'The Service may monitor and filter user-generated content.',
    'terms.3.item.deletion': 'All content may be deleted without prior notice if it violates internal policies or laws.',
    'terms.3.item.features': 'The Service provides the following features to users:',
    'terms.3.item.flagging': 'The ability to report inappropriate content (Flagging)',
    'terms.3.item.blocking': 'The ability to block specific users (Blocking)',
    'terms.4.title': '4. Disclaimer',
    'terms.4.item.liability': 'The Service is not liable for legal liability for content written by users, and such liability lies entirely with the user.',
    'terms.5.title': '5. Not Medical or Psychological Advice',
    'terms.5.item.no.advice': 'The Service does not provide professional medical or psychological advice',
    'terms.5.item.seek.help': 'Seek professional help for health-related issues',
    'terms.5.item.own.risk': 'Use of the Service is at your own risk',
    'terms.6.title': '6. Service Changes and Termination',
    'terms.6.item.modify': 'We may change or discontinue all or part of the Service',
    'terms.6.item.terminate': 'Accounts may be restricted or terminated for violations of these Terms',
    'terms.7.title': '7. Limitation of Liability',
    'terms.7.item.indirect': 'We are not liable for indirect or incidental damages',
    'terms.7.item.cap': 'To the extent permitted by law, our total liability is limited to amounts you paid in the last 3 months (or 0 if none)',
    'terms.8.title': '8. Governing Law and Disputes',
    'terms.8.item.law': 'These Terms are governed by the laws of the Republic of Korea',
    'terms.8.item.dispute': 'Disputes will be resolved by the competent courts',
    'terms.9.title': '9. Contact',
    'terms.9.item.email': 'Email: hello@pocotalk.app'
  },
  es: {
    'app.title': 'PocoTalk',
    'nav.home': 'Inicio',
    'nav.chat': 'Alma',
    'nav.activity': 'Actividad',
    'nav.routine': 'Rutina',
    'nav.emotion.release': 'Liberación de Emociones',
    'nav.privacy': 'Política de Privacidad',
    'nav.terms': 'Términos de uso',
    'language.selector': 'Seleccionar Idioma',
    'welcome.title': 'Bienvenido a PocoTalk',
    'welcome.subtitle': 'Explora emociones y cuida tu mente con IA',
    'chat.placeholder': 'Ingresa tu mensaje...',
    'chat.send': 'Enviar',
    'chat.typing': 'Alma está respondiendo...',
    'activity.select': 'Selecciona una actividad',
    'activity.complete': 'Completar',
    'routine.title': 'Rutina Completada',
    'routine.emotion': 'Emoción',
    'routine.activity': 'Actividad',
    'routine.continue': 'Continuar a Alma',
    'error.api': 'Ocurrió un error durante la llamada API',
    'error.network': 'Ocurrió un error de red',
    'home.main.title': 'Tiempo para calmar tu mente y encontrar paz',
    'home.main.subtitle': 'Organiza tus emociones diarias y encuentra paz interior con PocoTalk',
    'home.soul.title': 'Habla con Alma',
    'home.soul.subtitle': 'Ten una conversación cálida con tu amigo \'Alma\'',
    'home.recommended.title': 'Rutinas Recomendadas',
    'home.recommended.subtitle': 'Comienza rutinas que hagan tu día más significativo',
    'home.start': 'Comenzar',
    'home.routine.start': '¡Rutina Iniciada!',
    'home.routine.preparing': 'Preparando conversación con Alma',
    'home.wait': 'Por favor espera un momento...',
    'home.morning.routine': 'Poco Mañana',
    'home.morning.description': 'Tiempo para comenzar tu propio día',
    'home.focus.routine': 'Poco Enfoque',
    'home.focus.description': 'Tiempo para calmar tu mente y concentrarte',
    'home.evening.routine': 'Poco Gracias',
    'home.evening.description': 'Tiempo para terminar el día con gratitud',
    'chat.welcome': '¡Hola! Soy Alma. ¿Cómo estuvo tu día hoy? Siéntete libre de hablar conmigo.',
    'chat.start.conversation': 'Inicia una conversación con Alma',
    'chat.emotion.detected': 'Emoción detectada',
    'chat.emotion.expression': '¿Qué actividad te gustaría usar para expresar tu emoción?',
    'chat.ai.soul.description': 'Ten una conversación cálida con tu amigo \'Alma\'',
    'activity.title': 'Actividad',
    'activity.subtitle': 'Expresa y resuelve emociones a través de diversas actividades',
    'activity.completion.title': '¡Completado!',
    'activity.completion.emotion.resolved': 'resolvió {emotion} a través de {activity}',
    'activity.completion.summary': 'Resumen de Actividad',
    'activity.completion.continue': 'Continuar a Alma',
    'activity.completion.another': 'Probar Otra Actividad',
    'activity.sketch.title': 'Boceto (Próximamente)',
    'activity.sketch.title.coming': 'La función de boceto se agregará pronto',
    'activity.voice.title': 'Grabación de Voz (Próximamente)',
    'activity.voice.title.coming': 'La función de grabación de voz se agregará pronto',
    'activity.meditation.name': 'Meditación',
    'activity.meditation.description': 'Organiza tu mente y encuentra paz',
    'activity.breathing.name': 'Respiración Profunda',
    'activity.breathing.description': 'Calma tu mente con respiración profunda',
    'activity.piano.name': 'Piano',
    'activity.piano.description': 'Expresa emociones a través de la música',
    'activity.sketch.name': 'Boceto',
    'activity.sketch.description': 'Dibuja tu mente con imágenes',
    'activity.voice.name': 'Voz',
    'activity.voice.description': 'Graba emociones con tu voz',
    'activity.writing.name': 'Escritura',
    'activity.writing.description': 'Organiza pensamientos a través de la escritura',
    'routine.subtitle': 'Comienza una rutina básica y chatea con Alma',
    'routine.start': 'Comenzar',
    'routine.starting': '¡Comenzado!',
    'routine.preparing': 'Preparando conversación con Alma...',
    'routine.morning.title': 'Poco Mañana',
    'routine.morning.description': 'Tiempo para comenzar tu propio día',
    'routine.focus.title': 'Poco Enfoque',
    'routine.focus.description': 'Tiempo para calmar tu mente y concentrarte',
    'routine.evening.title': 'Poco Noche',
    'routine.evening.description': 'Tiempo para terminar el día con gratitud',
    'popup.coming.soon.title': 'Próximamente...',
    'popup.coming.soon.description': 'El servicio estará disponible pronto',
    // Privacy Policy
    'privacy.title': 'Política de Privacidad',
    'privacy.1.title': 'Artículo 1 (Elementos de información personal recopilados)',
    'privacy.1.item.required': 'Elementos requeridos: Correo electrónico, apodo, contraseña (almacenamiento encriptado)',
    'privacy.1.item.optional': 'Elementos opcionales: Foto de perfil, registros de emociones, publicaciones (texto, imágenes, audio, etc.)',
    'privacy.1.item.auto': 'Elementos recopilados automáticamente: Información del dispositivo, registros de acceso, cookies, registros de uso',
    'privacy.2.title': 'Artículo 2 (Propósito del uso de la información personal)',
    'privacy.2.item.service': 'Prestación y operación del servicio',
    'privacy.2.item.content': 'Publicación y compartición de contenido del usuario',
    'privacy.2.item.monitoring': 'Monitoreo y bloqueo de contenido inapropiado',
    'privacy.2.item.improvement': 'Mejora del servicio y soporte al cliente',
    'privacy.3.title': 'Artículo 3 (Retención y eliminación de información personal)',
    'privacy.3.item.delete': 'La información personal se elimina inmediatamente al retirar la cuenta',
    'privacy.3.item.law': 'Sin embargo, si la retención es requerida por ley, se sigue en consecuencia',
    'privacy.4.title': 'Artículo 4 (Derechos del usuario)',
    'privacy.4.item.access': 'Los usuarios pueden ver, modificar y eliminar su información personal en cualquier momento',
    'privacy.4.item.deletion': 'Los usuarios pueden solicitar la eliminación de la cuenta y la retirada del servicio si lo desean',
    'privacy.5.title': 'Artículo 5 (Provisión a terceros y subcontratación)',
    'privacy.5.item.principle': 'El servicio no proporciona la información personal de los usuarios a terceros en principio',
    'privacy.5.item.entrust': 'Sin embargo, algunas funciones como servidores en la nube y análisis de datos pueden ser subcontratadas a empresas externas',
    'privacy.6.title': 'Artículo 6 (Medidas de seguridad)',
    'privacy.6.item.security': 'El servicio gestiona de forma segura la información personal a través de encriptación, control de acceso y monitoreo de seguridad',
    'privacy.7.title': 'Artículo 7 (Eliminación de cuenta)',
    'privacy.7.item.menu': 'Los usuarios pueden eliminar su cuenta en cualquier momento a través del menú de la aplicación [Configuración → Editar Perfil → Eliminar Cuenta]',
    'privacy.7.item.immediate': 'Toda la información personal se elimina inmediatamente cuando se elimina la cuenta',
    // Terms of Service
    'terms.title': 'Términos de uso',
    'terms.intro': 'Al usar el Servicio, aceptas estos Términos y Política de Privacidad, y si no aceptas, no puedes usar el Servicio.',
    'terms.1.title': '1. Propósito',
    'terms.1.item.purpose': 'Este Acuerdo tiene como objetivo regular los derechos, obligaciones y responsabilidades entre la Empresa y los usuarios en relación con el uso del Servicio.',
    'terms.2.title': '2. Obligaciones del Usuario',
    'terms.2.item.prohibited': 'Los usuarios no deben realizar las siguientes acciones:',
    'terms.2.item.illegal': 'Contenido ilegal, obsceno, ofensivo, violento o discriminatorio',
    'terms.2.item.rights': 'Acciones que infringen los derechos de la información personal, derechos de autor, etc.',
    'terms.2.item.spam': 'Spam, publicaciones como anuncios o abuso del sistema',
    'terms.2.item.consequence': 'Las violaciones pueden resultar en restricciones en el uso del servicio y responsabilidad legal.',
    'terms.3.title': '3. Gestión de Contenido Generado por el Usuario',
    'terms.3.item.monitoring': 'El Servicio puede monitorear y filtrar el contenido generado por los usuarios.',
    'terms.3.item.deletion': 'Todo el contenido puede ser eliminado sin previo aviso si viola las políticas internas o las leyes.',
    'terms.3.item.features': 'El Servicio proporciona las siguientes funciones a los usuarios:',
    'terms.3.item.flagging': 'La capacidad de reportar contenido inapropiado (Flagging)',
    'terms.3.item.blocking': 'La capacidad de bloquear usuarios específicos (Bloqueo)',
    'terms.4.title': '4. Descargo de responsabilidad',
    'terms.4.item.liability': 'El Servicio no es responsable de la responsabilidad legal por el contenido escrito por los usuarios, y dicha responsabilidad recae en su totalidad en el usuario.',
    'terms.5.title': '5. No es Asesoramiento Médico o Psicológico',
    'terms.5.item.no.advice': 'El Servicio no proporciona asesoramiento médico o psicológico profesional',
    'terms.5.item.seek.help': 'Busca ayuda profesional para problemas de salud',
    'terms.5.item.own.risk': 'El uso del Servicio es bajo tu propio riesgo',
    'terms.6.title': '6. Cambios y Terminación del Servicio',
    'terms.6.item.modify': 'Podemos cambiar o discontinuar total o parcialmente el Servicio',
    'terms.6.item.terminate': 'Las cuentas pueden restringirse o cerrarse por violaciones de estos Términos',
    'terms.7.title': '7. Limitación de Responsabilidad',
    'terms.7.item.indirect': 'No somos responsables de daños indirectos o incidentales',
    'terms.7.item.cap': 'En la medida permitida por la ley, nuestra responsabilidad total se limita a los importes pagados por ti en los últimos 3 meses (o 0 si no hubo)',
    'terms.8.title': '8. Ley Aplicable y Disputas',
    'terms.8.item.law': 'Estos Términos se rigen por las leyes de la República de Corea',
    'terms.8.item.dispute': 'Las disputas se resolverán ante los tribunales competentes',
    'terms.9.title': '9. Contacto',
    'terms.9.item.email': 'Correo electrónico: hello@pocotalk.app'
  }
};

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState('ko');

  const t = (key: string): string => {
    const translations = TRANSLATIONS[language as keyof typeof TRANSLATIONS] || TRANSLATIONS.ko;
    return translations[key as keyof typeof translations] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}; 